import { eff } from "@/core/utils/effect-ts-abbr";
import { useI18n } from "@/shared/composables/use-i18n";
import axios from "axios";
import { useToast } from "primevue/usetoast";
import { shallowRef, watchEffect } from "vue";
import { setup, Vue } from "vue-class-component";
import { Model, Prop } from "vue-property-decorator";

export type AddressFieldModel = {
    readonly street?: string;
    readonly number?: string;
    readonly detail?: string | null;
    readonly postal_code?: string | null;
    readonly city?: string;
    readonly country_iso2?: string;
};

export class AppAddressFieldComponent extends Vue {
    @Model("modelValue", { required: true, default: {} })
    model!: AddressFieldModel;
    toast = setup(useToast);
    i18n = setup(useI18n);

    addressSearchFiber: eff.option.Option<eff.fiber.FiberContext<never, void>> =
        eff.option.none;
    addressPredictions: readonly google.maps.places.AutocompletePrediction[] = [];
    @Prop()
    address?: string;

    place?: string | null | google.maps.places.AutocompletePrediction = null;
    addressDetails?: google.maps.places.PlaceResult = setup(() => shallowRef());

    get addressComponents(): AddressFieldModel {
        return eff.fn.pipe(
            this.addressDetails,
            eff.option.fromNullable,
            eff.option.fold(
                () => ({}),
                (addressDetails) => ({
                    city: eff.fn.pipe(
                        addressDetails.address_components || [],
                        eff.array.findFirst(({ types }) => types.includes("locality")),
                        eff.option.map(({ long_name }) => long_name),
                        eff.option.toUndefined,
                    ),
                    postal_code: eff.fn.pipe(
                        addressDetails.address_components || [],
                        eff.array.findFirst(({ types }) => types.includes("postal_code")),
                        eff.option.map(({ long_name }) => String(long_name)),
                        eff.option.toUndefined,
                    ),
                    street: eff.fn.pipe(
                        addressDetails.address_components || [],
                        eff.array.findFirst(({ types }) => types.includes("route")),
                        eff.option.map(({ long_name }) => long_name),
                        eff.option.toUndefined,
                    ),
                    country_iso2: eff.fn.pipe(
                        addressDetails.address_components || [],
                        eff.array.findFirst(({ types }) => types.includes("country")),
                        eff.option.map(({ short_name }) => short_name),
                        eff.option.toUndefined,
                    ),
                    number: eff.fn.pipe(
                        addressDetails.address_components || [],
                        eff.array.findFirst(({ types }) =>
                            types.includes("street_number"),
                        ),
                        eff.option.map(({ short_name }) => short_name),
                        eff.option.toUndefined,
                    ),
                }),
            ),
        );
    }

    mounted() {
        this.place = this.address;

        watchEffect(() => {
            eff.fn.pipe(
                this.place,
                eff.option.fromNullable,
                eff.option.chain((place) =>
                    typeof place === "string" ? eff.option.none : eff.option.some(place),
                ),
                eff.option.fold(
                    () => eff.t.succeed(undefined),
                    ({ place_id }) =>
                        eff.fn.pipe(
                            eff.t.promise(() =>
                                axios.get<{
                                    readonly html_attributions: readonly string[];
                                    readonly info_messages: readonly string[];
                                    readonly result: google.maps.places.PlaceResult;
                                    readonly status:
                                        | "OK"
                                        | "ZERO_RESULTS"
                                        | "INVALID_REQUEST"
                                        | "OVER_QUERY_LIMIT"
                                        | "REQUEST_DENIED"
                                        | "UNKNOWN_ERROR";
                                }>("/maps/api/place/details/json", {
                                    params: {
                                        place_id,
                                    },
                                    validateStatus: () => true,
                                }),
                            ),

                            eff.t.chain((data) =>
                                eff.t.succeedWith(() => {
                                    if (
                                        data.data.status === "OK" ||
                                        data.data.status === "ZERO_RESULTS"
                                    ) {
                                        this.addressDetails = data.data.result;
                                    } else {
                                        this.toast.add({
                                            severity: "error",
                                            summary: this.i18n.t(
                                                "common.somethingWentWrong",
                                            ),
                                            life: 3000,
                                        });
                                    }
                                }),
                            ),
                            eff.t.runFiber,
                        ),
                ),
            );
        });

        watchEffect(() => {
            this.model = this.addressComponents;
        });
    }

    searchAddress({
        originalEvent: event,
        query: _query,
    }: {
        readonly originalEvent: UIEvent;
        readonly query: string;
    }) {
        if (!event.target || !(event.target instanceof HTMLInputElement)) {
            return;
        }

        const interruptPrevious = eff.fn.pipe(
            this.addressSearchFiber,
            eff.option.fold(
                () => eff.t.succeed(undefined),
                (fiber) => eff.fn.pipe(fiber, eff.fiber.interrupt),
            ),
        );

        const searchText = event.target.value || "";
        const offset = event.target.selectionStart || undefined;

        this.addressSearchFiber = eff.fn.pipe(
            eff.t.gen(function* (_) {
                yield* _(interruptPrevious);

                yield* _(eff.t.sleep(300));

                return yield* _(
                    eff.t.promise(() =>
                        axios.get<{
                            readonly predictions: google.maps.places.AutocompletePrediction[];
                            readonly status:
                                | "OK"
                                | "ZERO_RESULTS"
                                | "INVALID_REQUEST"
                                | "OVER_QUERY_LIMIT"
                                | "REQUEST_DENIED"
                                | "UNKNOWN_ERROR";
                            readonly error_message?: string;
                            readonly info_messages?: readonly string[];
                        }>("/maps/api/place/autocomplete/json", {
                            params: {
                                input: searchText,
                                offset,
                            },
                            validateStatus: () => true,
                        }),
                    ),
                );
            }),
            eff.t.chain((data) =>
                eff.t.succeedWith(() => {
                    if (
                        data.data.status === "OK" ||
                        data.data.status === "ZERO_RESULTS"
                    ) {
                        this.addressPredictions = data.data.predictions;
                    } else {
                        this.toast.add({
                            severity: "error",
                            summary: this.i18n.t("common.somethingWentWrong"),
                            life: 3000,
                        });
                    }
                }),
            ),
            eff.t.runFiber,
            eff.option.some,
        );
    }
}
